import { CartLineT, LineItemType } from '@lambda/apis/src/cart/types';
import { ShippingSKU } from '@lambda/apis/src/shipping/types';
import { DeliveryEstimateT } from '@lambda/apis/src/catalog-v2/types';
import { ProductCategoryWarranty } from '@lambda/apis/src/category/types';
import { useMemo } from 'react';
import classNames from 'classnames';
import { CartWarrantyT } from '@/lib/reebelocare/types';
import { useCartContext } from '@/utils/hooks/cart/useCartContext';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import { isPurchaseFlowEnabled } from '@/lib/purchase-flow';
import useOrderDiscounts from '@/lib/order-discounts/useOrderDiscounts';
import { getCartDiscountSubtotal } from '@/lib/order-discounts/helpers';
import OrderDiscountBadge from '@/components/order-discounts/OrderDiscountBadge';
import LineItem from './LineItem';
import { ToBeRemovedT } from '@/utils/hooks/cart/types';
import {
  trackCartUpdateModalDismissed,
  trackCartUpdateSimilarOffersClicked,
} from '@/lib/analytics/trackCart';
import OutOfStockPanel from './OutOfStockPanel';
import TradeInBanner from '@/components/tradein/TradeInBanner';
import {
  groupLinesBySku,
  isOrphanedRecommendation,
  orderByFirstAdded,
} from '../helpers';
import i18n from '@/settings/i18n';

const t = i18n.init('cart');

const LeftSection = ({
  removedCartLines,
  onDismiss,
  hasOutOfStockItems,
  expressShippingOffers,
  deliveryEstimates,
  reebeloCareSubtotal,
  warranties,
  categoryWarranties,
}: {
  removedCartLines: ToBeRemovedT[];
  onDismiss: (typeId: string) => void;
  hasOutOfStockItems: boolean;
  expressShippingOffers: Record<string, ShippingSKU>;
  deliveryEstimates: Record<string, DeliveryEstimateT[]>;
  reebeloCareSubtotal: number;
  warranties: CartWarrantyT;
  categoryWarranties: Record<string, ProductCategoryWarranty>;
}) => {
  const { cart, cartCount, cartLines } = useCartContext();
  const { customer } = useCustomerContext();
  // In the purchase flow an accessory is its own line item, wherever it was
  // added from; otherwise one added under a device shows in that device's
  // "Frequently bought together" instead (unless the device has gone).
  const isPurchaseFlow = isPurchaseFlowEnabled(customer?.email);
  // The purchase flow lists items in the order they were added (oldest
  // first); otherwise as MsCart returns them (newest first).
  const orderCards = (groups: CartLineT[][]) =>
    isPurchaseFlow ? orderByFirstAdded(groups, cartLines) : groups;

  const discountSubtotal = useMemo(
    () => getCartDiscountSubtotal(cartLines, reebeloCareSubtotal),
    [cartLines, reebeloCareSubtotal],
  );
  const orderDiscount = useOrderDiscounts(discountSubtotal, customer?.email);

  function onDismissClick(typeId: string) {
    if (cart) {
      const trackedProducts = removedCartLines
        .filter((item) => item.line.typeId === typeId)
        .map(({ line }) => ({
          line_item_id: line.id,
          offer_pk: line.typeId,
          vendor: line.vendor,
          sku: line.sku,
          psku: line.psku,
          name: line.title,
        }));

      trackCartUpdateModalDismissed({
        cart,
        properties: {
          cart_id: cart.id,
          store: cart.store,
          products: trackedProducts,
        },
      });
    }

    onDismiss(typeId);
  }

  function onSimilarOffersClick(typeId: string) {
    if (cart) {
      const trackedProducts = removedCartLines
        .filter((item) => item.line.typeId === typeId)
        .map(({ line }) => ({
          line_item_id: line.id,
          offer_pk: line.typeId,
          vendor: line.vendor,
          sku: line.sku,
          psku: line.psku,
          name: line.title,
        }));

      trackCartUpdateSimilarOffersClicked({
        cart,
        properties: {
          cart_id: cart.id,
          store: cart.store,
          products: trackedProducts,
        },
      });
    }

    onDismiss(typeId);
  }

  return (
    <section className="text-sm lg:col-span-7 xl:col-span-8">
      <div className="flex min-h-7 items-center justify-between gap-2">
        <h1
          className={classNames(
            'flex items-center gap-1.5 text-base',
            isPurchaseFlow ? 'font-bold' : 'font-medium',
          )}
        >
          {t`Your Cart`}
          {!isPurchaseFlow && (
            <span className="text-sm font-normal text-gray-700/60">
              ({cartCount} {cartCount === 1 ? t`item` : t`items`})
            </span>
          )}
        </h1>
        <OrderDiscountBadge state={orderDiscount} className="lg:hidden" />
      </div>
      <OutOfStockPanel
        removedCartLines={removedCartLines}
        hasOutOfStockItems={hasOutOfStockItems}
        onDismiss={onDismissClick}
        onSimilarOffers={onSimilarOffersClick}
        className=""
      />
      <div className="mt-2 flex flex-col gap-5">
        {orderCards(
          groupLinesBySku(
            cartLines
              // Filter out shipping and warranty lines as they are displayed within the LineItem component for each product
              .filter(
                (line) =>
                  ![LineItemType.SHIPPING, LineItemType.WARRANTY].includes(
                    line.type,
                  ) &&
                  (isPurchaseFlow ||
                    line?.notes?.find((note) => note.name === '_type')
                      ?.value !== 'CART_RECOMMENDATION' ||
                    isOrphanedRecommendation(line, cartLines)),
              ),
            // One card per SKU: the same device from several sellers arrives as
            // several lines, and they belong together.
          ),
        ).map((lines) => (
          <LineItem
            reebeloCareSubtotal={reebeloCareSubtotal}
            key={`line-item-${lines[0].id}`}
            lines={lines}
            expressShippingOffers={expressShippingOffers}
            deliveryEstimates={deliveryEstimates}
            warranties={warranties}
            categoryWarranties={categoryWarranties}
          />
        ))}
      </div>
      {/* The purchase flow has it in the Summary instead. */}
      {!isPurchaseFlow && (
        <TradeInBanner className="mt-5" showHeading={false} />
      )}
    </section>
  );
};

export default LeftSection;
